import { useId, useRef, useState, type ChangeEvent, type DragEvent, type MouseEvent } from 'react';

import Icons from '@/components/shared/icon';
import '@/components/forms/files.css';

/* Au-delà, le proxy rejette la requête sans réponse exploitable : on bloque le fichier dès la sélection. */
export const MAX_FILE_SIZE_MO = 10;

/** Pièces déposées, indexées par code de document (`C`, `CC`, `RIB`…). */
export type FilesValue = Record<string, File | undefined>;

/**
 * `A` est le nom du champ qui porte l'objet des pièces : le `setValue` du formulaire appelant
 * n'est accepté que si ce champ existe et peut recevoir un `FilesValue`.
 */
export interface FilesProps<A extends string = string> {
    label?: string;
    value?: FilesValue | null;
    setValue: (name: A, value: FilesValue) => void;
    /** Code du document dans l'objet des pièces. */
    code: string;
    name?: string;
    /** Nom du champ du formulaire qui porte l'objet des pièces. */
    arrayName: A;
    accept?: string;
}

/** Icône « document choisi », reprise à l'identique du `FileSelector` de react-rainbow. */
const FileIcon = () => (
    <svg width="14px" height="17px" viewBox="0 0 14 17" version="1.1">
        <g id="components" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <g id="Components-FileSelector-" transform="translate(-435.000000, -899.000000)">
                <g id="Group-46" transform="translate(419.000000, 863.000000)">
                    <g id="file" transform="translate(16.000000, 36.000000)">
                        <path
                            d="M0.433622047,8.41555118 L0.433622047,15.8444094 C0.433622047,15.9634252 0.530708661,16.0616142 0.649685039,16.0616142 L4.09775591,16.0616142 L9.20232283,16.0616142 L9.93582677,16.0616142 L12.9414961,16.0616142 C13.0604724,16.0616142 13.1575197,15.9634252 13.1575197,15.8444094 L13.1575197,12.3541339 L13.1575197,2.71279528 L13.1575197,2.61228346 L10.9627559,2.61228346 C10.751378,2.61228346 10.3124016,2.64232283 10.3124016,2.16287402 L10.3077559,0.0196850394 L0.649645669,0.0196850394 C0.530669291,0.0196850394 0.433622047,0.116732283 0.433622047,0.235748031 L0.433622047,2.09007874 L0.433622047,2.87456693 L0.433622047,8.41555118 L0.433622047,8.41555118 Z"
                            id="Path"
                            fill="#FFFFFF"
                        />
                        <path
                            d="M13.1574803,12.3541339 L13.1574803,2.71279528 L13.1574803,2.61228346 L13.1574803,2.57877953 C13.1574803,2.51637795 13.1309055,2.50598425 13.0350394,2.41240157 L10.6658661,0.152559055 C10.5538189,0.0462598425 10.5353543,0.0196850394 10.4694882,0.0196850394 L10.3077559,0.0196850394 L0.649645669,0.0196850394 C0.530669291,0.0196850394 0.433622047,0.116732283 0.433622047,0.235748031 L0.433622047,15.8444094 C0.433622047,15.9634252 0.530669291,16.0616142 0.649645669,16.0616142 L12.9414567,16.0616142 C13.0604331,16.0616142 13.1574803,15.9634252 13.1574803,15.8444094 L13.1574803,12.3541339 Z"
                            id="Path"
                            fill="#FFFFFF"
                        />
                        <path
                            d="M13.1574803,12.3541339 L13.1574803,2.71279528 L13.1574803,2.61228346 C13.1574803,2.49559055 13.1193701,2.49444882 13.0350394,2.41240157 L10.6658661,0.152559055 C10.5538189,0.0462598425 10.5353543,0.0196850394 10.4694882,0.0196850394 L10.3077559,0.0196850394 L7.90389764,0.0196850394 C10.8414173,3.95248031 11.0088976,11.0416535 8.82917323,15.4666142 C8.72866142,15.6722835 8.62354331,15.8698425 8.5138189,16.0616142 L9.20228346,16.0616142 L9.9357874,16.0616142 L12.9414567,16.0616142 C13.0604331,16.0616142 13.1574803,15.9634252 13.1574803,15.8444094 L13.1574803,12.3541339 Z"
                            id="Path"
                            fill="#F8F8F8"
                        />
                        <path
                            d="M10.3123622,2.16283465 C10.3123622,2.64228346 10.7512992,2.61224409 10.9627165,2.61224409 L13.1574803,2.61224409 L13.1574803,2.57874016 C13.1574803,2.51633858 13.1309055,2.50594488 13.0350394,2.4123622 L10.6658661,0.152519685 C10.5538189,0.0462204724 10.5353543,0.0196456693 10.4694882,0.0196456693 L10.3077559,0.0196456693 L10.3123622,2.16283465 Z"
                            id="Path"
                            fill="#FFFFFF"
                        />
                        <path
                            d="M13.0350394,2.41240157 L10.6658661,0.152559055 C10.5538189,0.0462598425 10.5353543,0.0196850394 10.4694882,0.0196850394 L10.3077559,0.0196850394 L10.3123622,2.16283465 C10.3123622,2.64228346 10.7512992,2.61224409 10.9627165,2.61224409 L13.1574803,2.61224409 L13.1574803,2.57874016 C13.1574803,2.51637795 13.1309055,2.50598425 13.0350394,2.41240157 Z"
                            id="Path"
                            fill="#B5C4CF"
                        />
                        <path
                            d="M11.2052756,4.74433071 L2.50826772,4.74433071 C2.34027559,4.74433071 2.20409449,4.60811024 2.20409449,4.44003937 C2.20409449,4.2719685 2.34027559,4.13574803 2.50826772,4.13574803 L11.2052756,4.13574803 C11.3732677,4.13574803 11.5094488,4.2719685 11.5094488,4.44003937 C11.5094488,4.60811024 11.3733071,4.74433071 11.2052756,4.74433071 Z"
                            id="Path"
                            fill="#9AAFB7"
                            fillRule="nonzero"
                        />
                        <path
                            d="M11.2052756,6.90594488 L2.50826772,6.90594488 C2.34027559,6.90594488 2.20409449,6.76972441 2.20409449,6.60165354 C2.20409449,6.43358268 2.34027559,6.2973622 2.50826772,6.2973622 L11.2052756,6.2973622 C11.3732677,6.2973622 11.5094488,6.43358268 11.5094488,6.60165354 C11.5094488,6.76972441 11.3733071,6.90594488 11.2052756,6.90594488 Z"
                            id="Path"
                            fill="#9AAFB7"
                            fillRule="nonzero"
                        />
                        <path
                            d="M11.2052756,9.06759843 L2.50826772,9.06759843 C2.34027559,9.06759843 2.20409449,8.93137795 2.20409449,8.76330709 C2.20409449,8.59523622 2.34027559,8.45901575 2.50826772,8.45901575 L11.2052756,8.45901575 C11.3732677,8.45901575 11.5094488,8.59523622 11.5094488,8.76330709 C11.5094488,8.93137795 11.3733071,9.06759843 11.2052756,9.06759843 Z"
                            id="Path"
                            fill="#9AAFB7"
                            fillRule="nonzero"
                        />
                        <path
                            d="M11.2052756,11.229252 L2.50826772,11.229252 C2.34027559,11.229252 2.20409449,11.0930315 2.20409449,10.9249606 C2.20409449,10.7568898 2.34027559,10.6206693 2.50826772,10.6206693 L11.2052756,10.6206693 C11.3732677,10.6206693 11.5094488,10.7568898 11.5094488,10.9249606 C11.5094488,11.0930315 11.3733071,11.229252 11.2052756,11.229252 Z"
                            id="Path"
                            fill="#9AAFB7"
                            fillRule="nonzero"
                        />
                        <path
                            d="M11.2052756,13.3909055 L2.50826772,13.3909055 C2.34027559,13.3909055 2.20409449,13.254685 2.20409449,13.0866142 C2.20409449,12.9185433 2.34027559,12.7823228 2.50826772,12.7823228 L11.2052756,12.7823228 C11.3732677,12.7823228 11.5094488,12.9185433 11.5094488,13.0866142 C11.5094488,13.254685 11.3733071,13.3909055 11.2052756,13.3909055 Z"
                            id="Path"
                            fill="#9AAFB7"
                            fillRule="nonzero"
                        />
                    </g>
                </g>
            </g>
        </g>
    </svg>
);

/** Icône d'annulation, reprise à l'identique du `FileSelector` de react-rainbow. */
const CancelIcon = () => (
    <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
        <title>cancel</title>
        <g id="components" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd" fillOpacity="0.7" opacity="0.583426339">
            <g id="Components-FileSelector-" transform="translate(-708.000000, -895.000000)" fill="#A8A9AF">
                <g id="Group-46" transform="translate(419.000000, 863.000000)">
                    <g id="Group-21" transform="translate(5.000000, 28.000000)">
                        <path
                            d="M296,4 C302.525457,4 307.834393,9.20713168 307.9962,15.69496 L308,16 C308,22.5254567 302.792868,27.8343928 296.30504,27.9962001 L296,28 C289.372583,28 284,22.6288742 284,16 C284,9.47454326 289.207132,4.16560725 295.69496,4.00379986 L296,4 Z M292.1465,12.1466667 C291.951167,12.3418333 291.951167,12.6585 292.1465,12.8536667 L295.290667,15.9976667 L292.1465,19.1416667 C291.951167,19.3368333 291.951167,19.6535 292.1465,19.8486667 C292.244167,19.9461667 292.372167,19.995 292.5,19.995 C292.627833,19.995 292.755833,19.9461667 292.8535,19.8485 L295.997667,16.7045 L299.141667,19.8485 C299.239333,19.9461667 299.367333,19.995 299.495167,19.995 C299.623,19.995 299.751,19.9461667 299.848667,19.8485 C300.044,19.6533333 300.044,19.3366667 299.848667,19.1415 L296.704667,15.9976667 L299.848667,12.8536667 C300.044,12.6585 300.044,12.3418333 299.848667,12.1466667 C299.653333,11.9513333 299.337,11.9513333 299.141667,12.1466667 L295.997667,15.2906667 L292.8535,12.1466667 C292.658167,11.9513333 292.341833,11.9513333 292.1465,12.1466667 Z"
                            id="cancel"
                        />
                    </g>
                </g>
            </g>
        </g>
    </svg>
);

/**
 * Dépôt d'une pièce justificative.
 *
 * Remplace le `FileSelector` (variante `multiline`) de react-rainbow-components, dont il reproduit
 * le balisage, les couleurs du thème par défaut (`#D7D9E2`, `#F6F7F9`, `#2A3039`, marque `#01b6f5`)
 * et le comportement : zone cliquable et déposable, icône d'appareil photo, nom du fichier choisi,
 * bouton d'annulation. Les surcharges de `files.css` (survol, couleur de l'icône, bouton
 * d'annulation contrasté) s'appliquent toujours : mêmes classes, même identifiant `cancel-button-*`.
 */
const Files = <A extends string>(props: FilesProps<A>) => {
    const { label, value, setValue, code, name, arrayName, accept } = props;
    const uid = useId().replace(/:/g, '');
    const inputRef = useRef<HTMLInputElement>(null);
    const [files, setFiles] = useState<FileList | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const buttonId = `cancel-button-${uid}`;
    // Valeur du formulaire : la pièce de ce code, ou `null` quand il n'y en a pas.
    const current = value && value[code] ? value[code] : null;

    const updateForm = (list: FileList) => {
        // Mutation en place de l'objet du formulaire, comme auparavant : les autres pièces
        // déjà déposées sont conservées.
        const file = list[0];
        const tooLarge = !!file && file.size > MAX_FILE_SIZE_MO * 1024 * 1024;
        setError(tooLarge ? `Ce fichier est trop volumineux (${MAX_FILE_SIZE_MO} Mo maximum).` : null);

        const arrayValues = (value ?? {}) as FilesValue;
        if (tooLarge) {
            delete arrayValues[code];
        } else {
            arrayValues[code] = file;
        }
        setValue(arrayName, arrayValues);
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const list = event.target.files ?? new DataTransfer().files;
        setFiles(list);
        updateForm(list);
    };

    const handleCancel = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const empty = new DataTransfer().files;
        setFiles(empty);
        if (inputRef.current) inputRef.current.value = '';
        updateForm(empty);
    };

    const handleDragLeave = (event: DragEvent<HTMLInputElement>) => {
        if (!event.relatedTarget || (event.relatedTarget as HTMLElement).id !== buttonId) {
            setIsDragOver(false);
        }
    };

    const isFileSelected = current !== null && !!files && files.length > 0;
    const text = isFileSelected ? (files.length === 1 ? files[0]!.name : `${files.length} files`) : label;
    const showFileIcon = isFileSelected && !isDragOver;
    const shouldRenderCancel = isFileSelected && !isDragOver;

    return (
        <div>
            <div
                className="inline-block mr-6 w-full h-full files-input"
                style={{ maxWidth: '100%' }}
            >
                <div
                    id={`dropzone-${uid}`}
                    className={`relative flex flex-col justify-center items-center w-full h-full min-h-[12rem] p-1 rounded-[27px] border border-dashed border-[#D7D9E2] bg-[#F6F7F9] text-[#2A3039] hover:cursor-pointer hover:border-[#01b6f5] ${hasFocus ? 'outline-0 border-solid border-[#01b6f5] shadow-[0_0_2px_#01b6f5]' : ''} ${isDragOver ? 'text-[#01b6f5] bg-[#01b6f5]/5 border-[#01b6f5]' : ''}`}
                >
                    <input
                        type="file"
                        id={`input-${uid}`}
                        name={name}
                        accept={accept || 'image/*,application/pdf'}
                        onChange={handleChange}
                        onFocus={() => setHasFocus(true)}
                        onBlur={() => setHasFocus(false)}
                        onDragEnter={() => setIsDragOver(true)}
                        onDragLeave={handleDragLeave}
                        onDrop={(event) => {
                            setIsDragOver(false);
                            setFiles(event.nativeEvent.dataTransfer?.files ?? null);
                        }}
                        ref={inputRef}
                        className="absolute top-0 left-0 w-full h-full opacity-0 hover:cursor-pointer"
                    />
                    <div className="flex flex-col justify-center items-center w-full h-full p-4 rounded-[12rem] bg-transparent text-center text-[0.875rem] pointer-events-none">
                        <span className="static z-[2] flex justify-center items-center w-16 h-auto mb-2 leading-none text-[#A4A7B5] [&_*:not(button)]:pointer-events-none [&_svg]:size-16! [&_svg]:text-[64px]! [&_svg]:text-[#01b6f5]">
                            {showFileIcon ? <FileIcon /> : <Icons icon={`camera`} fontSize={65} />}
                        </span>
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full pointer-events-none">{text}</span>
                        {shouldRenderCancel && (
                            <span className="absolute top-0 right-[0.3rem] z-[2] flex justify-center items-center w-[22px] h-full leading-none text-[#A4A7B5] [&_svg]:pointer-events-none">
                                <button
                                    type="button"
                                    id={buttonId}
                                    data-id="button-icon-element"
                                    aria-label="Supprimer le fichier"
                                    title="Supprimer le fichier"
                                    onClick={handleCancel}
                                    className="absolute top-3 right-[2px] mr-1.5 flex justify-center items-center rounded-[12rem] border-0 p-0 text-white transition-all cursor-pointer pointer-events-auto"
                                >
                                    <CancelIcon />
                                </button>
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {error && <p className="text-xs text-destructive mt-1">{error}</p>}
            <p className="text-xs text-muted-foreground mt-1">Format des fichiers autorisés : PDF, JPG, PNG — {MAX_FILE_SIZE_MO} Mo maximum</p>
        </div>
    );
};

export default Files;
